import React, {useContext, useEffect, useState} from 'react'
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {operationUrl, PRIVATE_URL} from "../../common/struct/urlManager";
import ListSnackbar, {TAlert} from "../../patterns/list/listSnackbar";
import {TOperation} from "../../interfaces/operation";
import {StoreContext} from "../../common/struct/store";
import {AppContext} from "../../App";
import MailIcon from "@mui/icons-material/Mail";
import Page from "../../patterns/page/page";
import {Box} from "@mui/material";
import {ReactComponent as PremiumSvg} from "../../assets/premium.svg";
import {addErrorAlert, addSuccessAlert} from "../../patterns/list/listUtils";
import OperationMailingHistory from "./operationMailingHistory";
import ListOperationEmailTemplates from "./listOperationEmailTemplates";
import {PrimaryBigButton, SecondaryBigButton} from "../../components/buttons/mainButton";
import BlockedTemplateCreationDialog from "../../patterns/dialog/blockedTemplateCreationDialog";
import InviteAllParticipants from "../operationParticipants/listParticipants/actions/inviteAllParticipants";
import SendTestEmail from "../operationMailing/sendTestEmail";
import {getEmailTemplate} from "../../services/AdminService";
import {getOperation, sendEmailInvitationsToUninvitedParticipants, sendTestEmailInvitation} from "../../services/OperationService";
import {errorManager, manageStringError} from "../../common/methods/ApiService";
import {IconPosition} from "../../components/buttons/buttonProps";
import {globalStoreReducer} from "../../common/methods/context-setter/globalStoreReducer";
import {CURRENT_OPERATION} from "../../common/methods/context-setter/globals";
import OperationMailingPlanned from "./operationMailingPlanned";
import {StickyNote2} from "@mui/icons-material";
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import Panel, {PanelSize} from "../../patterns/panel/panel";
import ListEmails from './listEmails';
import moment from "moment/moment";
import Footer from '../../patterns/footer/footer';
import {TEmailTemplate, TEmailTemplateType} from '../../interfaces/email';
import {DefaultEmailTemplate} from './defaultEmailTemplates';
import EmailTemplateSelector from './EmailTemplateSelector';
import EmailTemplateFormPanel from './emailTemplateFormPanel';
import OperationMailingSettings from './operationMailingSettings';
import {isSuperAdmin} from "../../common/struct/globalVar";
import CancelTemplateCreationDialog from "../../patterns/dialog/cancelTemplateCreationDialog";

export enum OperationMailingMenu {
  CUSTOMISATION,
  HISTORY = 2,
  PLANNED = 3,
  SETTING = 4,
}

const PremiumIcon = (): JSX.Element => <PremiumSvg width={24} height={24} />;

export default function OperationMailing(props: { setOpenCookieDialog: (openCookieDialog: boolean) => void }): JSX.Element {
  const {operationId} = useParams<string>();
  const {setOpenCookieDialog} = props;
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const extractSelectedMenu = (): OperationMailingMenu | null => {
    switch (location.pathname) {
    case operationUrl(PRIVATE_URL.OPERATION_EMAIL_CUSTOMISATION, operationId!):
      return OperationMailingMenu.CUSTOMISATION;
    case operationUrl(PRIVATE_URL.OPERATION_EMAIL_PLANNED, operationId!):
      return OperationMailingMenu.PLANNED;
    case operationUrl(PRIVATE_URL.OPERATION_EMAIL_HISTORY, operationId!):
      return OperationMailingMenu.HISTORY;
    case operationUrl(PRIVATE_URL.OPERATION_EMAIL_SETTINGS, operationId!):
      return OperationMailingMenu.SETTING;
    default:
      navigate(PRIVATE_URL.LANDING_PAGE);
      return null;
    }
  }
  const openPlannedEmailDetails = (sendAt: string): void => {
    setDetailsPanelSendAt(sendAt)
    setOpenDetailsPanel(true);
  };

  const [selectedMenu, setSelectedMenu] = useState(extractSelectedMenu());
  const [alerts, setAlerts] = useState<TAlert[]>([]);
  const [operation, setOperation] = useState<TOperation | undefined>(undefined);
  const [openInviteAllParticipants, setOpenInviteAllParticipants] = useState(false)
  const [openSendTestEmail, setOpenSendTestEmail] = useState(false)
  const [detailsPanelSendAt, setDetailsPanelSendAt] = useState<string>("")
  const [openDetailsPanel, setOpenDetailsPanel] = useState(false)
  const [emailTemplatePanelOpened, setEmailTemplatePanelOpened] = useState(false);
  const [emailTemplateToEdit, setEmailTemplateToEdit] = useState<TEmailTemplate | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);

  const [emailTemplateReload, setEmailTemplateReload] = useState(false);

  const [initialStep, setInitialStep] = useState<1 | 2>(1);

  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelDialogSave, setCancelDialogSave] = useState<(() => Promise<void>) | null>(null);
  const [cancelDialogValid, setCancelDialogValid] = useState(false);

  const handlePanelClose = (): void => {
    closeEmailTemplatePanel();
  };


  const handleCancelDialogSave = async (): Promise<void> => {
    await cancelDialogSave?.();
    setOpenCancelDialog(false);
  };

  const handleCancelDialogExit = (): void => {
    setOpenCancelDialog(false);
    closeEmailTemplatePanel();
  };

  const openEmailTemplatePanel = (template: TEmailTemplate | null, step: 1 | 2 = 1): void => {
    if (template?.id) {
      getEmailTemplate(template.id).then(fresh => {
        setEmailTemplateToEdit(fresh);
        setEmailTemplatePanelOpened(true);
        setInitialStep(step);
      });
    } else if (template) {
      setEmailTemplateToEdit(template);
      setShowTemplateSelector(false);
      setEmailTemplatePanelOpened(true);
    } else {
      setShowTemplateSelector(true);
      setEmailTemplateToEdit(null);
      setEmailTemplatePanelOpened(true);
      setInitialStep(step);
    }
  }

  const closeEmailTemplatePanel = (): void => {
    setEmailTemplateToEdit(null);
    setShowTemplateSelector(false);
    setEmailTemplatePanelOpened(false);
  }

  const handleSelectCustom = (): void => {
    setShowTemplateSelector(false);
  };

  const handleSelectDefault = (defaultTemplate: DefaultEmailTemplate): void => {
    const constructed: TEmailTemplate = {
      id: '',
      '@id': '',
      createdAt: '',
      updatedAt: '',
      company: {} as any,
      templateType: TEmailTemplateType.CUSTOM_TEMPLATE,
      mailjetId: 0,
      operation: {} as any,
      name: defaultTemplate.name,
      subject: defaultTemplate.subject,
      mailSender: defaultTemplate.mailSender ?? '',
      newPointAdded: false,
      newParticipantAdded: false,
      beginOperationAdded: false,
      extraVariables: {},
      components: defaultTemplate.components.map(c => ({
        ...c,
        id: crypto.randomUUID(),
        emailTemplateId: '',
      })),
    };
    setEmailTemplateToEdit(constructed);
    setShowTemplateSelector(false);
  };

  const closeDetailsPanel = (): void => setOpenDetailsPanel(false);

  const STORE = useContext<StoreContext>(AppContext);
  const [store] = STORE;

  useEffect(() => {
    setOperation(store.global.currentOperation);
  }, [store]);

  const menus = [
    {
      icon: MailIcon,
      label: 'operations.mailing.customisation.title',
      url: operationUrl(PRIVATE_URL.OPERATION_EMAIL_CUSTOMISATION, operationId!)
    },
    {
      icon: StickyNote2,
      label: 'operations.mailing.history.title',
      url: operationUrl(PRIVATE_URL.OPERATION_EMAIL_HISTORY, operationId!)
    },
    {
      icon: ScheduleSendIcon,
      label: 'operations.mailing.planned.title',
      url: operationUrl(PRIVATE_URL.OPERATION_EMAIL_PLANNED, operationId!)
    }
  ]

  if (isSuperAdmin()) {
    menus.push({
      icon: SettingsIcon,
      label: 'operations.mailing.settings.title',
      url: operationUrl(PRIVATE_URL.OPERATION_EMAIL_SETTINGS, operationId!)
    });
  }

  useEffect(() => {
    setSelectedMenu(extractSelectedMenu());
  }, [location]);

  const setError = (error: string): void => addErrorAlert(error, setAlerts)
  const setSuccess = (success: string): void => addSuccessAlert(success, setAlerts)
  const closeInviteAllParticipants = (): void => setOpenInviteAllParticipants(false)
  const closeSendTestEmail = (): void => setOpenSendTestEmail(false)
  const sendMails = (sendAt: string): Promise<void> => {
    return sendEmailInvitationsToUninvitedParticipants(operationId!, sendAt)
      .then(response => {
        addSuccessAlert(t("operation_participants.action_invite_success", {count: response.sent.count}), setAlerts);
      })
      .then(() => getOperation(operationId!))
      .then(operation => {
        globalStoreReducer(STORE, {type: CURRENT_OPERATION, operation: operation});
        closeInviteAllParticipants();
      })
      .catch(error => {
        manageStringError(errorManager(error, t, STORE, navigate), (error: string) => addErrorAlert(error, setAlerts), t);
      })
  }

  const [openBlockedTemplateCreation, setOpenBlockedTemplateCreation] = useState(false);
  const templateCreationActivated = store.global.company.templateCreationActivated || isSuperAdmin();

  const primaryButton = templateCreationActivated
    ? <PrimaryBigButton key="create-email-template" sx={{ml: 2}} icon={MailIcon} position={IconPosition.LEFT}
      action={(): void => openEmailTemplatePanel(null)}
      label={t("email_templates.actions.create")} />
    : <SecondaryBigButton key="create-email-template" icon={PremiumIcon} position={IconPosition.LEFT}
      sx={{
        ml: 2, backgroundColor: 'premium.main', color: '#15144B', border: 'none',
        ":hover": {backgroundColor: 'premium.light', color: '#15144B', border: 'none'}
      }}
      action={(): void => setOpenBlockedTemplateCreation(true)}
      label={t("email_templates.actions.create")} />;

  const sendTestEmails = (): Promise<void> => {
    return sendTestEmailInvitation(operationId!)
      .then(() => {
        addSuccessAlert(t("operation_participants.action_test_invite_success"), setAlerts);
      })
      .then(() => closeSendTestEmail())
      .catch(error => {
        manageStringError(errorManager(error, t, STORE, navigate), (error: string) => addErrorAlert(error, setAlerts), t);
      })
  }

  return (
    <>
      {openSendTestEmail && <SendTestEmail key="send-test-email" openDialog={openSendTestEmail} closeDialog={closeSendTestEmail} sendMail={sendTestEmails} operation={operation} />}
      {openInviteAllParticipants && <InviteAllParticipants key="invite-all-participants" openDialog={openInviteAllParticipants} closeDialog={closeInviteAllParticipants} sendMail={sendMails} operation={operation} />}
      <BlockedTemplateCreationDialog open={openBlockedTemplateCreation} close={(): void => setOpenBlockedTemplateCreation(false)} />
      {openDetailsPanel && detailsPanelSendAt && <Panel key="email-details" size={PanelSize.BIG} title={t("operation_mailing_planned.details_panel.title", {date: moment(detailsPanelSendAt).format("DD/MM/YYYY à HH:mm")})} open={openDetailsPanel} close={closeDetailsPanel}><ListEmails sendAt={detailsPanelSendAt} /></Panel>}
      {emailTemplatePanelOpened && (
        <Panel
          title={t("email_templates.actions.create")}
          size={PanelSize.XXL}
          open={emailTemplatePanelOpened}
          close={handlePanelClose}>
          {showTemplateSelector ? (
            <EmailTemplateSelector
              onSelectCustom={handleSelectCustom}
              onSelectDefault={handleSelectDefault}
              onCancel={closeEmailTemplatePanel}
              addError={setError} />
          ) : (
            <EmailTemplateFormPanel
              key={emailTemplateToEdit?.id ?? 'new'}
              template={emailTemplateToEdit}
              initialStep={initialStep}
              setClose={closeEmailTemplatePanel}
              setReload={setEmailTemplateReload}
              addSuccess={setSuccess}
              addError={setError}
              setCancelDialogSave={setCancelDialogSave}
              setCancelDialogValid={setCancelDialogValid}
              setOpenCancelDialog={setOpenCancelDialog} />
          )}
        </Panel>
      )}
      <CancelTemplateCreationDialog
        open={openCancelDialog}
        setOpen={setOpenCancelDialog}
        setClose={handleCancelDialogExit}
        onSave={handleCancelDialogSave}
        isValid={cancelDialogValid}
      />
      <Page title={t("operations.mailing.title")} menus={menus} backLink={operationUrl(PRIVATE_URL.OPERATION_CUSTOMISATION, operationId!)}
        buttons={[primaryButton]}>
        <ListSnackbar alerts={alerts} setAlerts={setAlerts} />
        {operation == null ? <></> :
          <Box sx={{px: 8, display: "flex", flexDirection: "column", justifyContent: "space-around", flex: 1, py: 4}}>
            <Box sx={{display: "flex", flexDirection: "column", flex: 1}}>
              {selectedMenu === OperationMailingMenu.CUSTOMISATION && <ListOperationEmailTemplates onEdit={openEmailTemplatePanel} reload={emailTemplateReload} setReload={setEmailTemplateReload} />}
              {selectedMenu === OperationMailingMenu.HISTORY && <OperationMailingHistory operation={operation} setError={setError} setSuccess={setSuccess} />}
              {selectedMenu === OperationMailingMenu.PLANNED && <OperationMailingPlanned operation={operation} setError={setError} setSuccess={setSuccess} openPlannedEmailDetails={openPlannedEmailDetails} />}
              {selectedMenu === OperationMailingMenu.SETTING && <OperationMailingSettings setError={setError} setSuccess={setSuccess} />}
            </Box>
            <Footer setOpenCookieDialog={setOpenCookieDialog} />
          </Box>
        }
      </Page>
    </>
  )
}
